import React, { useState, useEffect, useRef } from 'react';

/**
 * A button that asks for a second tap instead of opening window.confirm.
 *
 * Native confirm() is unreliable here: installed PWAs and some mobile browsers
 * suppress or auto-dismiss it, and a dismissed dialog returns false — so the
 * action silently never ran and the control looked broken. Confirming in the
 * page always works and reads better on a phone.
 */
export default function ConfirmButton({
  onConfirm,
  label,
  confirmLabel = 'Tap again to confirm',
  className = '',
  style,
  confirmStyle,
  timeoutMs = 6000,
  'aria-label': ariaLabel,
}) {
  const [armed, setArmed] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  function click() {
    if (!armed) {
      setArmed(true);
      // Disarm on its own so a stray tap cannot sit primed indefinitely.
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setArmed(false), timeoutMs);
      return;
    }
    clearTimeout(timer.current);
    setArmed(false);
    onConfirm();
  }

  return (
    <button
      type="button"
      onClick={click}
      aria-label={ariaLabel || label}
      className={className}
      style={armed ? { ...style, ...confirmStyle } : style}
    >
      {armed ? confirmLabel : label}
    </button>
  );
}
